import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';

import { environment } from '../../environments/environment';

export interface PublicRestaurant {
  id: number;
  name: string;
}

export interface PublicTable {
  id: number;
  name: string;
  number: number | null;
  capacity: number | null;
  area: string | null;
  status: string;
}

export interface PublicProduct {
  id: number;
  categoryId: number | null;
  name: string;
  description: string | null;
  price: number | string;
  imageUrl: string | null;
  isActive: boolean;
}

export interface PublicOrderItem {
  id: number;
  productId: number;
  quantity: number;
  unitPrice: number | string;
  total: number | string;
  notes: string | null;
  product?: {
    id: number;
    name: string;
    description?: string | null;
    imageUrl?: string | null;
  };
}

export interface PublicOrder {
  id: number;
  status: string;
  source: string;
  subtotal: number | string;
  total: number | string;
  notes?: string | null;
  createdAt: string;
  items: PublicOrderItem[];
}

export interface SessionSummary {
  subtotal: number;
  serviceFeeRate: number;
  serviceFeeAmount: number;
  discountAmount: number;
  total: number;
  paidAmount: number;
  remainingAmount: number;
}

export interface PublicSession {
  id: number;
  status: string;
  customerName?: string | null;
  customerCount?: number | null;
  openedAt: string;
  billRequestedAt?: string | null;
  orders: PublicOrder[];
  summary: SessionSummary;
}

export interface PublicTableResponse {
  restaurant: PublicRestaurant;
  table: PublicTable;
  session: PublicSession | null;
  products: PublicProduct[];

  permissions: {
    canOrder: boolean;
    canRequestBill: boolean;
  };
}

export interface CreatePublicOrderPayload {
  customerName?: string;
  customerPhone?: string;
  notes?: string;

  items: Array<{
    productId: number;
    quantity: number;
    notes?: string;
  }>;
}

@Injectable({
  providedIn: 'root'
})
export class PublicTableService {
  private readonly apiUrl = environment.apiUrl;

  constructor(private readonly http: HttpClient) {}

  getTable(publicToken: string): Observable<PublicTableResponse> {
    return this.http.get<PublicTableResponse>(
      `${this.apiUrl}/public/tables/${encodeURIComponent(publicToken)}`
    );
  }

  getOrders(publicToken: string): Observable<any> {
    return this.http.get(
      `${this.apiUrl}/public/tables/${encodeURIComponent(publicToken)}/orders`
    );
  }

  createOrder(
    publicToken: string,
    payload: CreatePublicOrderPayload
  ): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/public/tables/${encodeURIComponent(publicToken)}/orders`,
      payload
    );
  }

  requestBill(publicToken: string): Observable<any> {
    return this.http.post(
      `${this.apiUrl}/public/tables/${encodeURIComponent(publicToken)}/request-bill`,
      {}
    );
  }
}